import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { State } from "../base/types";
import {
  Appeal,
  AppealByOrder,
  AppealByOrderIdResponse,
  AppealForm,
  AppealResponse,
  CashRegisterSmall,
  createAppealApi,
  CreateAppealRequest,
  getAppeals as getAppealsApi,
  getAppealsByOrderIdApi,
  getMerchantAppealsApi,
  MerchantAppealForm,
  OrderAppeal,
  updateAppealApi,
  UpdateAppealApiRequest,
  UpdateAppealResponse,
  uploadReceiptApi,
  UploadReceiptRespnose,
} from "./api";

export type MergedAppeal = Appeal & {
  order_final_amount: number;
  order_final_amount_usdt: number;
  order_type: string;
  order_created_at: string;
  order_start_amount: number;
  order_start_amount_usdt: number;
  order_checker_message: string;
  order_invid: string;
  order_uuid: string;
  order_old_status: string;
  order_status: string;
  cash_register: CashRegisterSmall;
  bank: string;
};

type AppealsState = State<{
  orderAppeals: OrderAppeal[] | null;
  appeals: MergedAppeal[] | null;
  appealsByOrderId: AppealByOrderIdResponse | null;
  loadingByOrder: boolean;
  page: number;
  count: number;
  errorByOrderId: string | null;
  totalCount: number;
  lastAppealId: string | null;
  lastAppeal: AppealByOrder | null;
  isReceiptLoading: boolean;
  receiptUrls: string[];
}>;

export const getAppeals = createAsyncThunk<
  AppealResponse,
  { userId: string; data: AppealForm }
>("appeals/getAppeals", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await getAppealsApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getMerchantAppeals = createAsyncThunk<
  AppealResponse,
  { userId: string; data: MerchantAppealForm }
>(
  "appeals/getMerchantAppeals",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await getMerchantAppealsApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAppealsByOrderId = createAsyncThunk<
  AppealByOrderIdResponse,
  { orderId: string }
>("appeals/getAppealsByOrderId", async ({ orderId }, { rejectWithValue }) => {
  try {
    return await getAppealsByOrderIdApi(orderId);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const uploadReceipt = createAsyncThunk<
  UploadReceiptRespnose,
  { appealId: string; file: FileList }
>("orders/uploadReceipt", async ({ appealId, file }, { rejectWithValue }) => {
  try {
    return await uploadReceiptApi(appealId, file);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const createAppeal = createAsyncThunk<
  AppealByOrder,
  { data: CreateAppealRequest }
>("appeals/createAppeal", async ({ data }, { rejectWithValue }) => {
  try {
    return await createAppealApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateAppeal = createAsyncThunk<
  UpdateAppealResponse,
  { appealId: string; data: UpdateAppealApiRequest }
>("appeals/updateAppeal", async ({ data, appealId }, { rejectWithValue }) => {
  try {
    return await updateAppealApi(appealId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

const initialState: AppealsState = {
  orderAppeals: null,
  appeals: null,
  appealsByOrderId: null,
  loading: false,
  loadingByOrder: false,
  error: null,
  errorByOrderId: null,
  page: 1,
  count: 0,
  totalCount: 0,
  lastAppealId: null,
  lastAppeal: null,
  isReceiptLoading: false,
  receiptUrls: [],
};

const appealsSlice = createSlice({
  name: "appeals",
  initialState,
  reducers: {
    setLastAppealId: (state, action: PayloadAction<string | null>) => {
      state.lastAppealId = action.payload;
    },
    resetLastAppeal: (state) => {
      state.lastAppealId = null;
      state.lastAppeal = null;
      state.receiptUrls = [];
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.receiptUrls = state.receiptUrls.filter(
        (url) => url !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAppeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppeals.fulfilled, (state, action) => {
        state.loading = false;
        state.orderAppeals = action.payload.order_appeals;
        state.appeals = action.payload.order_appeals
          .flatMap((order) =>
            order.appeals.map((appeal) => ({
              ...appeal,
              order_uuid: order.order_uuid,
              order_invid: order.order_invid,
              order_final_amount: order.order_final_amount,
              order_final_amount_usdt: order.order_final_amount_usdt,
              order_type: order.order_type,
              order_created_at: order.order_created_at,
              order_start_amount: order.order_start_amount,
              order_start_amount_usdt: order.order_start_amount_usdt,
              order_checker_message: order.order_checker_message,
              order_old_status: order.order_old_status,
              order_status: order.order_status,
              cash_register: order.cash_register,
              bank: order.bank,
            }))
          )
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
          .sort((a, b) => {
            if (a.status === "processing" && b.status !== "processing")
              return -1;
            if (a.status !== "processing" && b.status === "processing")
              return 1;
            return 0;
          });
        state.page = action.payload.page;
        state.count = action.payload.count;
        state.totalCount = action.payload.total_count;
      })
      .addCase(getAppeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getMerchantAppeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMerchantAppeals.fulfilled, (state, action) => {
        state.loading = false;
        state.orderAppeals = action.payload.order_appeals;
        state.appeals = action.payload.order_appeals
          .flatMap((order) =>
            order.appeals.map((appeal) => ({
              ...appeal,
              order_uuid: order.order_uuid,
              order_invid: order.order_invid,
              order_final_amount: order.order_final_amount,
              order_final_amount_usdt: order.order_final_amount_usdt,
              order_type: order.order_type,
              order_created_at: order.order_created_at,
              order_start_amount: order.order_start_amount,
              order_start_amount_usdt: order.order_start_amount_usdt,
              order_checker_message: order.order_checker_message,
              order_old_status: order.order_old_status,
              order_status: order.order_status,
              cash_register: order.cash_register,
              bank: order.bank,
            }))
          )
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
          .sort((a, b) => {
            if (a.status === "processing" && b.status !== "processing")
              return -1;
            if (a.status !== "processing" && b.status === "processing")
              return 1;
            return 0;
          });
        state.page = action.payload.page;
        state.count = action.payload.count;
        state.totalCount = action.payload.total_count;
      })
      .addCase(getMerchantAppeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAppealsByOrderId.pending, (state) => {
        state.loadingByOrder = true;
        state.errorByOrderId = null;
      })
      .addCase(getAppealsByOrderId.fulfilled, (state, action) => {
        state.loadingByOrder = false;
        state.appealsByOrderId = action.payload;
        let lastAppeal = null;
        if (action.payload.appeals.length > 0) {
          lastAppeal = action.payload.appeals.reduce((latest, current) => {
            return new Date(current.updated_at) > new Date(latest.updated_at)
              ? current
              : latest;
          });
        }
        state.lastAppeal = lastAppeal;
      })
      .addCase(getAppealsByOrderId.rejected, (state, action) => {
        state.loadingByOrder = false;
        state.errorByOrderId = action.payload as string;
      })
      .addCase(uploadReceipt.pending, (state) => {
        state.isReceiptLoading = true;
      })
      .addCase(uploadReceipt.fulfilled, (state, action) => {
        state.isReceiptLoading = false;
        if (state.lastAppeal != null) {
          const result = [];
          if (state.lastAppeal.check_image_links != null) {
            result.push(...state.lastAppeal.check_image_links);
          }
          result.push(...action.payload.urls);
          state.lastAppeal.check_image_links = result;
        }
        state.receiptUrls = [...state.receiptUrls, ...action.payload.urls];
      })
      .addCase(createAppeal.pending, (state) => {
        state.loadingByOrder = false;
        state.error = null;
      })
      .addCase(createAppeal.fulfilled, (state, action) => {
        console.log(action.payload);
        state.loadingByOrder = false;
      })
      .addCase(createAppeal.rejected, (state, action) => {
        state.loadingByOrder = false;
        state.error = action.payload as string;
      })
      .addCase(updateAppeal.fulfilled, (state, action) => {
        state;
        console.log(action.payload);
      })
      .addCase(updateAppeal.rejected, (state, action) => {
        // state.loadingByOrder = false;
        state.error = action.payload as string;
      });
  },
});

export const { setLastAppealId, resetLastAppeal, removeFile } =
  appealsSlice.actions;
export default appealsSlice.reducer;
